import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Users, Car, Calendar, DollarSign, TrendingUp, AlertTriangle,
    ArrowUpRight, ArrowDownRight, Activity, Clock, Star, Map
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'

export default function AdminDashboard() {
    useTranslation()
    const { language } = useLanguageStore()

    // Stats data - would come from API
    const stats = {
        users: { total: 1245, new: 23, growth: 12 },
        vehicles: { total: 156, pending: 8, growth: 5 },
        bookings: { total: 342, active: 28, growth: 18 },
        revenue: { total: 125000000, today: 8500000, growth: 15 },
    }

    const recentActivities = [
        { type: 'booking', message: 'New booking #1234 by John Smith', time: '5 min ago' },
        { type: 'user', message: 'New user registered: Nguyễn Văn A', time: '15 min ago' },
        { type: 'vehicle', message: 'Vehicle approved: Honda Air Blade 160', time: '1 hour ago' },
        { type: 'withdrawal', message: 'Withdrawal request from owner Trần Thị B', time: '2 hours ago' },
        { type: 'review', message: 'New 5-star review for Toyota Vios', time: '3 hours ago' },
    ]

    const pendingItems = [
        { type: 'vehicles', count: 8, label: { en: 'Vehicles pending approval', vi: 'Xe chờ duyệt' }, link: '/admin/vehicles?status=pending' },
        { type: 'withdrawals', count: 3, label: { en: 'Withdrawal requests', vi: 'Yêu cầu rút tiền' }, link: '/admin/withdrawals' },
        { type: 'reviews', count: 12, label: { en: 'Reviews to moderate', vi: 'Đánh giá chờ duyệt' }, link: '/admin/reviews?status=pending' },
    ]

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value)
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">
                    {language === 'en' ? 'Dashboard' : 'Tổng Quan'}
                </h1>
                <p className="text-gray-500 mt-1">
                    {language === 'en' ? 'Overview of your rental platform' : 'Tổng quan nền tảng cho thuê'}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Users Card */}
                <div className="bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className={`flex items-center text-sm font-medium ${stats.users.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {stats.users.growth >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                            {Math.abs(stats.users.growth)}%
                        </span>
                    </div>
                    <p className="text-2xl font-bold mt-4">{stats.users.total.toLocaleString()}</p>
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Users' : 'Tổng người dùng'}</p>
                    <p className="text-xs text-blue-600 mt-1">+{stats.users.new} {language === 'en' ? 'new this week' : 'mới tuần này'}</p>
                </div>

                {/* Vehicles Card */}
                <div className="bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <Car className="w-6 h-6 text-green-600" />
                        </div>
                        <span className="flex items-center text-sm font-medium text-green-600">
                            <ArrowUpRight className="w-4 h-4" />
                            {stats.vehicles.growth}%
                        </span>
                    </div>
                    <p className="text-2xl font-bold mt-4">{stats.vehicles.total}</p>
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Vehicles' : 'Tổng số xe'}</p>
                    <p className="text-xs text-orange-600 mt-1">{stats.vehicles.pending} {language === 'en' ? 'pending approval' : 'chờ duyệt'}</p>
                </div>

                {/* Bookings Card */}
                <div className="bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-purple-600" />
                        </div>
                        <span className="flex items-center text-sm font-medium text-green-600">
                            <ArrowUpRight className="w-4 h-4" />
                            {stats.bookings.growth}%
                        </span>
                    </div>
                    <p className="text-2xl font-bold mt-4">{stats.bookings.total}</p>
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Bookings' : 'Tổng đặt xe'}</p>
                    <p className="text-xs text-green-600 mt-1">{stats.bookings.active} {language === 'en' ? 'active now' : 'đang hoạt động'}</p>
                </div>

                {/* Revenue Card */}
                <div className="bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-yellow-600" />
                        </div>
                        <span className="flex items-center text-sm font-medium text-green-600">
                            <TrendingUp className="w-4 h-4" />
                            {stats.revenue.growth}%
                        </span>
                    </div>
                    <p className="text-2xl font-bold mt-4">{formatCurrency(stats.revenue.total)}</p>
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Revenue' : 'Tổng doanh thu'}</p>
                    <p className="text-xs text-green-600 mt-1">+{formatCurrency(stats.revenue.today)} {language === 'en' ? 'today' : 'hôm nay'}</p>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pending Actions */}
                <div className="bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        <h2 className="font-bold text-lg">{language === 'en' ? 'Pending Actions' : 'Cần Xử Lý'}</h2>
                    </div>
                    <div className="space-y-3">
                        {pendingItems.map((item) => (
                            <Link
                                key={item.type}
                                to={item.link}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                            >
                                <span className="text-gray-700">{item.label[language]}</span>
                                <span className="px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                                    {item.count}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="lg:col-span-2 bg-white rounded-xl p-5 shadow-sm border">
                    <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-5 h-5 text-blue-500" />
                        <h2 className="font-bold text-lg">{language === 'en' ? 'Recent Activity' : 'Hoạt Động Gần Đây'}</h2>
                    </div>
                    <div className="space-y-3">
                        {recentActivities.map((activity, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${activity.type === 'booking' ? 'bg-purple-100' :
                                        activity.type === 'user' ? 'bg-blue-100' :
                                            activity.type === 'vehicle' ? 'bg-green-100' :
                                                activity.type === 'withdrawal' ? 'bg-yellow-100' :
                                                    'bg-orange-100'
                                    }`}>
                                    {activity.type === 'booking' && <Calendar className="w-4 h-4 text-purple-600" />}
                                    {activity.type === 'user' && <Users className="w-4 h-4 text-blue-600" />}
                                    {activity.type === 'vehicle' && <Car className="w-4 h-4 text-green-600" />}
                                    {activity.type === 'withdrawal' && <DollarSign className="w-4 h-4 text-yellow-600" />}
                                    {activity.type === 'review' && <Star className="w-4 h-4 text-orange-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-700">{activity.message}</p>
                                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                        <Clock className="w-3 h-3" />
                                        {activity.time}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link to="/admin/users" className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition text-center">
                    <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                    <p className="font-medium">{language === 'en' ? 'Manage Users' : 'Quản lý người dùng'}</p>
                </Link>
                <Link to="/admin/vehicles" className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition text-center">
                    <Car className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <p className="font-medium">{language === 'en' ? 'Manage Vehicles' : 'Quản lý xe'}</p>
                </Link>
                <Link to="/admin/tours" className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition text-center">
                    <Map className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                    <p className="font-medium">{language === 'en' ? 'Manage Tours' : 'Quản lý tour'}</p>
                </Link>
                <Link to="/admin/settings" className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition text-center">
                    <TrendingUp className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                    <p className="font-medium">{language === 'en' ? 'View Analytics' : 'Xem thống kê'}</p>
                </Link>
            </div>
        </div>
    )
}
